import { useState } from 'react'
import {
  GOLD_SET_NOTES_MAX_LENGTH,
  type GoldSetLabel,
  type GoldSetLabelInput,
  type GoldSetPhase,
} from '~/entities/gold-set'
import { Alert, Button, Text, Textarea, Tooltip } from '~/shared/ui'
import { toLabelInput, toReviewFormValues, type ReviewFormValues } from '../lib'
import { useSaveShortcut } from '../model'
import { CategoryPicker } from './category-picker'
import { YesNoField } from './yes-no-field'

export function ReviewForm({
  phases,
  pass,
  initialLabel,
  isSaving,
  saveError,
  canSkip,
  canGoBack,
  onSave,
  onSkip,
  onPrevious,
}: {
  phases: GoldSetPhase[]
  pass: number
  initialLabel: GoldSetLabel | null
  isSaving: boolean
  saveError: string | null
  canSkip: boolean
  canGoBack: boolean
  onSave: (label: GoldSetLabelInput) => void
  onSkip: () => void
  onPrevious: () => void
}) {
  const [values, setValues] = useState<ReviewFormValues>(() => toReviewFormValues(initialLabel))
  const labelInput = toLabelInput(values, pass)
  const canSave = !!labelInput && !isSaving

  const update = (next: Partial<ReviewFormValues>) =>
    setValues((current) => ({ ...current, ...next }))

  const save = () => {
    if (labelInput && !isSaving) onSave(labelInput)
  }

  useSaveShortcut(save)

  return (
    <div className="flex flex-col gap-5">
      <CategoryPicker
        phases={phases}
        value={values.category}
        onChange={(category) => update({ category })}
      />
      <YesNoField
        label="Sure?"
        value={values.sure}
        onChange={(sure) => update({ sure })}
      />
      <YesNoField
        label="More than one letter?"
        value={values.multipleLetters}
        onChange={(multipleLetters) => update({ multipleLetters })}
      />
      <div className="flex flex-col gap-1">
        <Text appearance="SUBTITLE2">Notes</Text>
        <Textarea
          rows={4}
          maxLength={GOLD_SET_NOTES_MAX_LENGTH}
          placeholder="One line on what made it hard, e.g. saw category, PA/claim?, EOB only, illegible, same date"
          value={values.notes}
          onChange={(event) => update({ notes: event.target.value })}
        />
        <Text
          className="text-text-secondary self-end"
          appearance="CAPTION"
        >
          {`${values.notes.length.toLocaleString('en-US')} / ${GOLD_SET_NOTES_MAX_LENGTH.toLocaleString('en-US')}`}
        </Text>
      </div>
      {saveError && (
        <Alert
          className="grow-0"
          appearance="ERROR"
          materialIcon="error"
        >
          {saveError}
        </Alert>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button
          disabled={!canGoBack || isSaving}
          onClick={onPrevious}
        >
          Previous
        </Button>
        <div className="flex items-center gap-2">
          <Tooltip
            content="No other document is left to do"
            open={canSkip ? false : undefined}
          >
            <span>
              <Button
                disabled={!canSkip || isSaving}
                onClick={onSkip}
              >
                Skip
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            content="Choose a category and answer both questions"
            open={labelInput ? false : undefined}
          >
            <span>
              <Button
                raised
                disabled={!canSave}
                onClick={save}
              >
                {isSaving ? 'Saving…' : initialLabel ? 'Update & next' : 'Save & next'}
              </Button>
            </span>
          </Tooltip>
        </div>
      </div>
      <Text
        className="text-text-secondary self-end"
        appearance="CAPTION"
      >
        Ctrl+Enter or ⌘+Enter saves
      </Text>
    </div>
  )
}
